import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import API from "../services/api";
import useI18n from "../i18n/useI18n";
import DashboardSkeleton from "./loaders/DashboardSkeleton";


const STUDIO_USAGE_REFRESH_KEY = "hookora_studio_usage_refresh";
const THEME_KEY = "hookora_theme";

const TOOL_TYPE_ALIASES = {
  hooks: "hooks",
  hook_generator: "hooks",
  viral_hooks: "hooks",
  viral_hook_generator: "hooks",

  youtube_title_optimizer: "youtube_title_optimizer",
  youtube_titles: "youtube_title_optimizer",
  youtube_title: "youtube_title_optimizer",

  caption_generator: "caption_generator",
  captions: "caption_generator",
  caption: "caption_generator",

  hashtag_generator: "hashtag_generator",
  hashtags: "hashtag_generator",
  hashtag: "hashtag_generator",

  description_rewriter: "description_rewriter",
  description_rewrite: "description_rewriter",
  descriptions: "description_rewriter",
  description: "description_rewriter",

  short_script_generator: "short_script_generator",
  short_scripts: "short_script_generator",
  short_script: "short_script_generator",

  thumbnail_text_analyzer: "thumbnail_text_analyzer",
  thumbnail_text: "thumbnail_text_analyzer",
  thumbnail_texts: "thumbnail_text_analyzer",

  hook_angles: "hook_angles",
  hook_angle: "hook_angles",

  cta_generator: "cta_generator",
  ctas: "cta_generator",
  cta: "cta_generator",

  humanizer: "humanizer",
  ai_humanizer: "humanizer",
  ai_humanizer_tool: "humanizer",
  humanized_rewrite: "humanizer",
  humanized_rewrites: "humanizer"
};

const normalizeToolType = (value) => {
  if (!value) return "other";
  const normalized = String(value)
    .trim()
    .toLowerCase()
    .replace(/-/g, "_")
    .replace(/\s+/g, "_");
  return TOOL_TYPE_ALIASES[normalized] || normalized;
};

const getDefaultToolMeta = (toolType, t) => {
  const normalized = normalizeToolType(toolType);

  switch (normalized) {
    case "hooks":
      return {
        label: t("dashboard.tools.hooks.label", "Viral Hook Generator"),
        badge: t("dashboard.tools.hooks.badge", "HOOKS")
      };
    case "youtube_title_optimizer":
      return {
        label: t(
          "dashboard.tools.youtubeTitleOptimizer.label",
          "YouTube Title Optimizer"
        ),
        badge: t("dashboard.tools.youtubeTitleOptimizer.badge", "TITLE")
      };
    case "caption_generator":
      return {
        label: t("dashboard.tools.captionGenerator.label", "Caption Generator"),
        badge: t("dashboard.tools.captionGenerator.badge", "CAPTION")
      };
    case "hashtag_generator":
      return {
        label: t("dashboard.tools.hashtagGenerator.label", "Hashtag Generator"),
        badge: t("dashboard.tools.hashtagGenerator.badge", "HASHTAG")
      };
    case "description_rewriter":
      return {
        label: t(
          "dashboard.tools.descriptionRewriter.label",
          "Description Rewriter"
        ),
        badge: t("dashboard.tools.descriptionRewriter.badge", "DESCRIPTION")
      };
    case "short_script_generator":
      return {
        label: t(
          "dashboard.tools.shortScriptGenerator.label",
          "Short Script Generator"
        ),
        badge: t("dashboard.tools.shortScriptGenerator.badge", "SCRIPT")
      };
    case "thumbnail_text_analyzer":
      return {
        label: t(
          "dashboard.tools.thumbnailTextAnalyzer.label",
          "Thumbnail Text Analyzer"
        ),
        badge: t("dashboard.tools.thumbnailTextAnalyzer.badge", "THUMBNAIL")
      };
    case "hook_angles":
      return {
        label: t("dashboard.tools.hookAngles.label", "Hook Angles"),
        badge: t("dashboard.tools.hookAngles.badge", "ANGLES")
      };
    case "cta_generator":
      return {
        label: t("dashboard.tools.ctaGenerator.label", "CTA Generator"),
        badge: t("dashboard.tools.ctaGenerator.badge", "CTA")
      };
    case "humanizer":
      return {
        label: t("dashboard.tools.humanizer.label", "AI Humanizer"),
        badge: t("dashboard.tools.humanizer.badge", "HUMANIZER")
      };
    default:
      return {
        label: t("dashboard.tools.other.label", "Other"),
        badge: t("dashboard.tools.other.badge", "OTHER")
      };
  }
};

const getToolMeta = (toolType, fallbackLabel, fallbackBadge, t) => {
  const normalized = normalizeToolType(toolType);
  const defaultMeta = getDefaultToolMeta(normalized, t);

  return {
    toolType: normalized,
    label: fallbackLabel || defaultMeta.label,
    badge: fallbackBadge || defaultMeta.badge
  };
};

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return `₦${amount.toLocaleString()}`;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { t } = useI18n();

  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [copiedHookId, setCopiedHookId] = useState(null);
  const [copiedRewardField, setCopiedRewardField] = useState("");
  const [platformFilter, setPlatformFilter] = useState("All");
  const [toolFilter, setToolFilter] = useState("All");
  const [sortBy, setSortBy] = useState("score");
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState("");
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [upgradePrompt, setUpgradePrompt] = useState("");
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const [theme, setTheme] = useState(getStoredTheme());
  const [welcomePopup, setWelcomePopup] = useState({
  visible: false,
  title: "",
  message: ""
});

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      pageBg: isDark ? "#020617" : "#f8fafc",
      pageGradient: isDark
        ? "radial-gradient(circle at top, rgba(59,130,246,0.12), transparent 30%), radial-gradient(circle at top right, rgba(124,58,237,0.1), transparent 25%)"
        : "none",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#374151",
      border: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      borderStrong: isDark ? "rgba(148,163,184,0.28)" : "#d1d5db",
      cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      cardBgStrong: isDark ? "rgba(15,23,42,0.92)" : "#ffffff",
      cardSubtle: isDark ? "rgba(30,41,59,0.7)" : "#f8fafc",
      shadow: isDark
        ? "0 14px 40px rgba(2, 6, 23, 0.45)"
        : "0 8px 24px rgba(15, 23, 42, 0.04)",
      shadowSoft: isDark
        ? "0 10px 26px rgba(2, 6, 23, 0.34)"
        : "0 4px 14px rgba(15, 23, 42, 0.03)",
      inputBg: isDark ? "#0f172a" : "#ffffff",
      inputText: isDark ? "#f8fafc" : "#111827",
      trackBg: isDark ? "#1e293b" : "#e5e7eb",
      lockedBg: isDark ? "rgba(88,28,135,0.18)" : "#faf5ff",
      lockedBorder: isDark ? "rgba(196,181,253,0.45)" : "#c4b5fd",
      lockedText: isDark ? "#e9d5ff" : "#6b21a8",
      purpleBg: isDark ? "rgba(91,33,182,0.22)" : "#f5f3ff",
      greenBg: isDark ? "rgba(6,95,70,0.22)" : "#d1fae5",
      greenText: isDark ? "#86efac" : "#065f46",
      lineAxis: isDark ? "rgba(148,163,184,0.28)" : "#d1d5db",
      bestBadgeBg: "#22c55e",
      favoriteBadgeBg: isDark ? "rgba(245,158,11,0.2)" : "#fef3c7",
      favoriteBadgeText: isDark ? "#fbbf24" : "#92400e",
      alertDangerBg: isDark ? "rgba(127,29,29,0.25)" : "#fef2f2",
      alertDangerBorder: isDark ? "rgba(248,113,113,0.35)" : "#fecaca",
      alertDangerText: isDark ? "#fecaca" : "#b91c1c",
      alertWarningBg: isDark ? "rgba(120,53,15,0.24)" : "#fffbeb",
      alertWarningBorder: isDark ? "rgba(251,191,36,0.34)" : "#fde68a",
      alertWarningText: isDark ? "#fcd34d" : "#92400e",
      alertInfoBg: isDark ? "rgba(8,47,73,0.28)" : "#ecfeff",
      alertInfoBorder: isDark ? "rgba(34,211,238,0.32)" : "#a5f3fc",
      buttonDarkBg: isDark ? "#e2e8f0" : "#111827",
      buttonDarkText: isDark ? "#020617" : "#ffffff",
      emptyStateBg: isDark ? "rgba(15,23,42,0.85)" : "#ffffff",
      founderBg: isDark
        ? "linear-gradient(135deg, rgba(30,41,59,0.96), rgba(15,23,42,0.98))"
        : "linear-gradient(135deg, #eef2ff, #f8fafc)",
      founderBorder: isDark ? "rgba(129,140,248,0.28)" : "#c7d2fe",
      founderPanelBg: isDark ? "rgba(15,23,42,0.82)" : "#ffffff",
      founderPanelSubtleBg: isDark ? "rgba(30,41,59,0.68)" : "#f8fafc",
      rewardsBg: isDark ? "rgba(15,23,42,0.86)" : "#ffffff",
      rewardsBorder: isDark ? "rgba(96,165,250,0.2)" : "#dbeafe",
      rewardsAccentBg: isDark ? "rgba(37,99,235,0.16)" : "#eff6ff",
      rewardsAccentBorder: isDark ? "rgba(96,165,250,0.24)" : "#bfdbfe",
      rewardsAccentText: isDark ? "#bfdbfe" : "#1d4ed8",
      rewardsSoftBg: isDark ? "rgba(30,41,59,0.72)" : "#f8fafc",
      rewardsSoftBorder: isDark ? "rgba(148,163,184,0.14)" : "#e5e7eb",
      walletBg: isDark ? "rgba(88,28,135,0.16)" : "#faf5ff",
      walletBorder: isDark ? "rgba(192,132,252,0.2)" : "#e9d5ff",
      walletText: isDark ? "#e9d5ff" : "#7c3aed",
      successButtonBg: "#2563eb",
      secondaryButtonBg: isDark ? "#1e293b" : "#e5e7eb",
      secondaryButtonText: isDark ? "#f8fafc" : "#111827",
      toolBlueBg: isDark ? "rgba(37,99,235,0.18)" : "#dbeafe",
      toolBlueText: isDark ? "#bfdbfe" : "#1d4ed8",
      toolBlueBorder: isDark ? "rgba(147,197,253,0.28)" : "#93c5fd",
      toolGreenBg: isDark ? "rgba(16,185,129,0.18)" : "#d1fae5",
      toolGreenText: isDark ? "#a7f3d0" : "#065f46",
      toolGreenBorder: isDark ? "rgba(110,231,183,0.28)" : "#86efac",
      toolPurpleBg: isDark ? "rgba(124,58,237,0.18)" : "#ede9fe",
      toolPurpleText: isDark ? "#ddd6fe" : "#6d28d9",
      toolPurpleBorder: isDark ? "rgba(196,181,253,0.28)" : "#c4b5fd",
      toolAmberBg: isDark ? "rgba(245,158,11,0.18)" : "#fef3c7",
      toolAmberText: isDark ? "#fde68a" : "#92400e",
      toolAmberBorder: isDark ? "rgba(252,211,77,0.28)" : "#fcd34d"
    }),
    [isDark]
  );

  const tt = useCallback(
  (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  },
  [t]
);

const getTimeBasedGreeting = useCallback(() => {
  const hour = new Date().getHours();

  if (hour < 12) return tt("dashboard.welcome.time.morning", "Good morning");
  if (hour < 17) return tt("dashboard.welcome.time.afternoon", "Good afternoon");
  if (hour < 21) return tt("dashboard.welcome.time.evening", "Good evening");
  return tt("dashboard.welcome.time.night", "Good evening");
}, [tt]);

const buildWelcomeMessage = useCallback(
  (username) => {
    const firstName = username
      ? String(username).split(" ")[0]
      : t("dashboard.welcome.creator", "Creator");

    const timeGreeting = getTimeBasedGreeting();

    const greetings = [
      {
        title: `${timeGreeting}, ${firstName} 👋`,
        message: t(
          "dashboard.welcome.messages.one",
          "Welcome back to Hookora. Let’s create something strong today."
        )
      },
      {
        title: `${firstName}, you’re back ⚡`,
        message: t(
          "dashboard.welcome.messages.two",
          "Your dashboard is ready. Let’s build sharper content."
        )
      },
      {
        title: `${timeGreeting}, ${firstName} ✨`,
        message: t(
          "dashboard.welcome.messages.three",
          "Good to see you again. Your creative workspace is waiting."
        )
      },
      {
        title: `${firstName}, welcome back 🚀`,
        message: t(
          "dashboard.welcome.messages.four",
          "Let’s turn today’s ideas into stronger hooks and content."
        )
      },
      {
        title: `${timeGreeting}, ${firstName} 💙`,
        message: t(
          "dashboard.welcome.messages.five",
          "Hookora is ready when you are."
        )
      }
    ];

    return greetings[Math.floor(Math.random() * greetings.length)];
  },
  [getTimeBasedGreeting, t]
);

  const cardStyle = useMemo(
    () => ({
      padding: "22px",
      border: `1px solid ${colors.border}`,
      borderRadius: "18px",
      background: colors.cardBg,
      boxShadow: colors.shadow,
      backdropFilter: isDark ? "blur(14px)" : "none"
    }),
    [colors, isDark]
  );

  const sectionTitleStyle = {
    color: colors.text,
    marginBottom: "14px"
  };

  const secondaryTextStyle = {
    color: colors.textMuted
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "10px",
    border: `1px solid ${colors.borderStrong}`,
    background: colors.inputBg,
    color: colors.inputText,
    boxSizing: "border-box",
    outline: "none"
  };

  const getToolBadgeStyle = useCallback(
    (toolType) => {
      const normalized = normalizeToolType(toolType);

      if (normalized === "hook_angles") {
        return {
          background: colors.toolGreenBg,
          color: colors.toolGreenText,
          border: `1px solid ${colors.toolGreenBorder}`
        };
      }

      if (normalized === "cta_generator") {
        return {
          background: colors.toolPurpleBg,
          color: colors.toolPurpleText,
          border: `1px solid ${colors.toolPurpleBorder}`
        };
      }

      if (normalized === "humanizer") {
        return {
          background: colors.toolAmberBg,
          color: colors.toolAmberText,
          border: `1px solid ${colors.toolAmberBorder}`
        };
      }

      return {
        background: colors.toolBlueBg,
        color: colors.toolBlueText,
        border: `1px solid ${colors.toolBlueBorder}`
      };
    },
    [colors]
  );

  const normalizeStudioAsset = useCallback(
    (asset) => {
      const meta = getToolMeta(asset?.tool_type, asset?.tool_label, asset?.tool_badge, t);

      return {
        ...asset,
        tool_type: meta.toolType,
        tool_label: meta.label,
        tool_badge: meta.badge
      };
    },
    [t]
  );

  const normalizeStudioUsageTool = useCallback(
    (item) => {
      const meta = getToolMeta(item?.tool_name, item?.tool_label, item?.tool_badge, t);

      return {
        ...item,
        tool_name: meta.toolType,
        tool_label: meta.label,
        tool_badge: meta.badge
      };
    },
    [t]
  );

  const normalizeTopToolType = useCallback(
    (item) => {
      const meta = getToolMeta(item?.tool_type, item?.tool_label, item?.tool_badge, t);

      return {
        ...item,
        tool_type: meta.toolType,
        tool_label: meta.label,
        tool_badge: meta.badge
      };
    },
    [t]
  );

  const normalizeCombinedActivityItem = useCallback(
    (item) => {
      if (item?.type !== "studio_asset") return item;
      return normalizeStudioAsset(item);
    },
    [normalizeStudioAsset]
  );

  const normalizeDashboardPayload = useCallback(
    (payload) => {
      if (!payload) return payload;

      const normalizedTopToolTypes = (payload.top_tool_types || []).map(
        normalizeTopToolType
      );
      const normalizedRecentStudioAssets = (payload.recent_studio_assets || []).map(
        normalizeStudioAsset
      );
      const normalizedCombinedActivity = (payload.combined_activity || []).map(
        normalizeCombinedActivityItem
      );

      const normalizedStudioUsageAnalytics = payload.studio_usage_analytics
        ? {
            ...payload.studio_usage_analytics,
            most_used_tool: normalizeToolType(
              payload.studio_usage_analytics.most_used_tool
            ),
            most_used_tool_label:
              payload.studio_usage_analytics.most_used_tool_label ||
              getToolMeta(
                payload.studio_usage_analytics.most_used_tool,
                null,
                null,
                t
              ).label,
            tools: (payload.studio_usage_analytics.tools || []).map(
              normalizeStudioUsageTool
            )
          }
        : payload.studio_usage_analytics;

      const normalizedStudioToolCounts = {};
      Object.entries(payload.metrics?.studio_tool_counts || {}).forEach(
        ([key, value]) => {
          const normalizedKey = normalizeToolType(key);
          normalizedStudioToolCounts[normalizedKey] =
            (normalizedStudioToolCounts[normalizedKey] || 0) + Number(value || 0);
        }
      );

      return {
        ...payload,
        metrics: {
          ...payload.metrics,
          studio_tool_counts: normalizedStudioToolCounts
        },
        studio_usage_analytics: normalizedStudioUsageAnalytics,
        top_tool_types: normalizedTopToolTypes,
        recent_studio_assets: normalizedRecentStudioAssets,
        combined_activity: normalizedCombinedActivity
      };
    },
    [
      normalizeCombinedActivityItem,
      normalizeStudioAsset,
      normalizeStudioUsageTool,
      normalizeTopToolType,
      t
    ]
  );

  const loadDashboard = useCallback(async () => {
    try {
      const [dashboardRes, analyticsRes, trendsRes, rewardsRes] = await Promise.all([
        API.get("/dashboard/"),
        API.get("/hooks/analytics"),
        API.get("/hooks/trends"),
        API.get("/referrals/me").catch(() => ({ data: null }))
      ]);

      setStats(normalizeDashboardPayload(dashboardRes.data));
      setAnalytics(analyticsRes.data);
      setTrends(trendsRes.data);
      setRewards(rewardsRes?.data || null);
      setError("");
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.status === 401
          ? t("dashboard.errors.notLoggedIn", "You are not logged in. Please log in first.")
          : t("dashboard.errors.loadFailed", "Failed to load dashboard.")
      );
    }
  }, [normalizeDashboardPayload, t]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    const handleStudioUsageRefresh = () => {
      loadDashboard();
    };

    const handleWindowFocus = () => {
      loadDashboard();
    };

    const handleStorage = (event) => {
      if (event.key === STUDIO_USAGE_REFRESH_KEY) {
        loadDashboard();
      }
    };

    window.addEventListener("hookora:studio-usage-updated", handleStudioUsageRefresh);
    window.addEventListener("focus", handleWindowFocus);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(
        "hookora:studio-usage-updated",
        handleStudioUsageRefresh
      );
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("storage", handleStorage);
    };
  }, [loadDashboard]);

  const showUpgradePrompt = (featureName) => {
    setUpgradePrompt(
      t(
        "dashboard.upgradePrompt.message",
        "{{featureName}} is available on the Pro plan. Upgrade to unlock this advanced analytics feature."
      ).replace("{{featureName}}", featureName)
    );
  };

  const copyHook = (hookText, hookId) => {
    navigator.clipboard
      .writeText(hookText)
      .then(() => {
        setCopiedHookId(hookId);
        setTimeout(() => setCopiedHookId(null), 2000);
      })
      .catch((err) =>
        console.error(t("dashboard.errors.copyFailedConsole", "Failed to copy:"), err)
      );
  };

  const copyRewardText = async (value, fieldName) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedRewardField(fieldName);
      setTimeout(() => setCopiedRewardField(""), 1800);
    } catch (err) {
      console.error(
        t("dashboard.errors.copyRewardFailedConsole", "Failed to copy reward text:"),
        err
      );
      setError(t("dashboard.errors.copyFailed", "Failed to copy. Please try again."));
    }
  };

  const toggleFavorite = async (hookId) => {
    try {
      setActionLoadingId(hookId);
      await API.patch(`/hooks/${hookId}/favorite`);
      await loadDashboard();
    } catch (err) {
      console.error(
        t("dashboard.errors.toggleFavoriteConsole", "Failed to toggle favorite:"),
        err
      );
      setError(
        t("dashboard.errors.favoriteUpdateFailed", "Failed to update favorite status.")
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  const deleteHook = async (hookId) => {
    const shouldDelete = window.confirm(
      t("dashboard.confirm.deleteHook", "Are you sure you want to delete this hook?")
    );
    if (!shouldDelete) return;

    try {
      setActionLoadingId(hookId);
      await API.delete(`/hooks/${hookId}`);
      await loadDashboard();
    } catch (err) {
      console.error(t("dashboard.errors.deleteHookConsole", "Failed to delete hook:"), err);
      setError(t("dashboard.errors.deleteHookFailed", "Failed to delete hook."));
    } finally {
      setActionLoadingId(null);
    }
  };

  const deleteStudioAsset = async (assetId) => {
    const shouldDelete = window.confirm(
      t(
        "dashboard.confirm.deleteStudioAsset",
        "Are you sure you want to delete this saved Studio asset?"
      )
    );
    if (!shouldDelete) return;

    try {
      setActionLoadingId(`asset-${assetId}`);
      await API.delete(`/studio-assets/${assetId}`);
      await loadDashboard();
    } catch (err) {
      console.error(
        t("dashboard.errors.deleteStudioAssetConsole", "Failed to delete Studio asset:"),
        err
      );
      setError(
        t("dashboard.errors.deleteStudioAssetFailed", "Failed to delete Studio asset.")
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  const regenerateHook = async (hookId) => {
    try {
      setActionLoadingId(hookId);
      await API.post(`/hooks/${hookId}/regenerate`, {
        template: "curiosity"
      });
      await loadDashboard();
    } catch (err) {
      console.error(
        t("dashboard.errors.regenerateConsole", "Failed to regenerate hook:"),
        err
      );
      setError(
        err?.response?.data?.detail ||
          t("dashboard.errors.regenerateFailed", "Failed to regenerate hook.")
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  const renderScoreBar = (score) => {
    const safeScore = typeof score === "number" ? score : 0;
    const color = safeScore >= 80 ? "#22c55e" : safeScore >= 50 ? "#f59e0b" : "#ef4444";

    return (
      <div
        style={{
          height: "10px",
          background: colors.trackBg,
          borderRadius: "999px",
          marginTop: "8px",
          overflow: "hidden"
        }}
      >
        <div
          style={{
            width: `${Math.max(0, Math.min(100, safeScore))}%`,
            height: "100%",
            backgroundColor: color,
            borderRadius: "999px",
            transition: "width 0.3s ease"
          }}
        />
      </div>
    );
  };

  const featureBadge = (label, color = "#7c3aed", bg = colors.purpleBg) => (
    <span
      style={{
        display: "inline-block",
        padding: "4px 8px",
        borderRadius: "999px",
        fontSize: "12px",
        fontWeight: "700",
        color,
        background: bg,
        marginLeft: "8px",
        marginTop: isMobile ? "6px" : 0,
        border: isDark ? `1px solid ${colors.border}` : "none"
      }}
    >
      {label}
    </span>
  );

  const formatToolName = (value) => {
    const meta = getToolMeta(value, null, null, t);
    return meta.label;
  };

  const formatPlanLabel = (plan) => {
    const normalized = (plan || "free").toLowerCase();
    if (normalized === "creator") return t("dashboard.plan.creator", "✔ Creator");
    if (normalized === "pro") return t("dashboard.plan.pro", "✔ Pro");
    return t("dashboard.plan.free", "Free");
  };

  const getPlanBadgeStyle = (plan) => {
    const normalized = (plan || "free").toLowerCase();

    if (normalized === "creator") {
      return {
        color: "#1d4ed8",
        background: isDark ? "rgba(29,78,216,0.18)" : "#dbeafe",
        border: `1px solid ${isDark ? "rgba(147,197,253,0.34)" : "#93c5fd"}`
      };
    }

    if (normalized === "pro") {
      return {
        color: "#8b5cf6",
        background: isDark ? "rgba(109,40,217,0.18)" : "#f3e8ff",
        border: `1px solid ${isDark ? "rgba(216,180,254,0.34)" : "#d8b4fe"}`
      };
    }

    return {
      color: isDark ? "#fbbf24" : "#92400e",
      background: isDark ? "rgba(245,158,11,0.18)" : "#fef3c7",
      border: `1px solid ${isDark ? "rgba(252,211,77,0.28)" : "#fcd34d"}`
    };
  };

  const buildLinePath = (values, width, height, padding) => {
    if (!values.length) return "";

    const maxValue = Math.max(...values, 1);
    const minValue = Math.min(...values, 0);
    const range = maxValue - minValue || 1;
    const innerWidth = width - padding * 2;
    const innerHeight = height - padding * 2;

    return values
      .map((value, index) => {
        const x =
          padding +
          (values.length === 1 ? innerWidth / 2 : (index / (values.length - 1)) * innerWidth);
        const y = padding + innerHeight - ((value - minValue) / range) * innerHeight;
        return `${index === 0 ? "M" : "L"} ${x} ${y}`;
      })
      .join(" ");
  };

  const buildPoints = (values, width, height, padding) => {
    if (!values.length) return [];

    const maxValue = Math.max(...values, 1);
    const minValue = Math.min(...values, 0);
    const range = maxValue - minValue || 1;
    const innerWidth = width - padding * 2;
    const innerHeight = height - padding * 2;

    return values.map((value, index) => {
      const x =
        padding +
        (values.length === 1 ? innerWidth / 2 : (index / (values.length - 1)) * innerWidth);
      const y = padding + innerHeight - ((value - minValue) / range) * innerHeight;
      return { x, y, value };
    });
  };

  const safeStats = useMemo(() => stats || {}, [stats]);
  const safeMetrics = useMemo(() => safeStats.metrics || {}, [safeStats]);
  const safeTopHooks = useMemo(() => safeStats.top_hooks || [], [safeStats]);
  const safeRecentHooks = useMemo(() => safeStats.recent_hooks || [], [safeStats]);
  const safeFavoriteHooks = useMemo(
    () => safeStats.favorite_hooks || [],
    [safeStats]
  );
  const safeRecentStudioAssets = useMemo(
    () => safeStats.recent_studio_assets || [],
    [safeStats]
  );
  const safeTopToolTypes = useMemo(
    () => safeStats.top_tool_types || [],
    [safeStats]
  );
  const safeCombinedActivity = useMemo(
    () => safeStats.combined_activity || [],
    [safeStats]
  );
  const safeUsage = useMemo(() => safeStats.usage || null, [safeStats]);
  const safeUsageMessages = useMemo(
    () => safeStats.usage_messages || null,
    [safeStats]
  );
  const safeFounderOverview = useMemo(
    () => safeStats.founder_overview || null,
    [safeStats]
  );
  const safeUser = useMemo(() => safeStats.user || {}, [safeStats]);
  const safeStudioUsageAnalytics = useMemo(
    () => safeStats.studio_usage_analytics || {},
    [safeStats]
  );

  useEffect(() => {
  if (!stats || error) return;
  if (typeof window === "undefined") return;

  const popupSessionKey = "hookora_dashboard_welcome_popup_seen";
  const alreadyShown = sessionStorage.getItem(popupSessionKey);

  if (alreadyShown) return;

  const username =
    safeUser?.username ||
    safeUser?.name ||
    safeUser?.email?.split("@")[0] ||
    "";

  const welcome = buildWelcomeMessage(username);

  const showTimer = setTimeout(() => {
    setWelcomePopup({
      visible: true,
      title: welcome.title,
      message: welcome.message
    });

    sessionStorage.setItem(popupSessionKey, "true");
  }, 2000);

  const hideTimer = setTimeout(() => {
    setWelcomePopup((prev) => ({
      ...prev,
      visible: false
    }));
  }, 7000);

  return () => {
    clearTimeout(showTimer);
    clearTimeout(hideTimer);
  };
}, [stats, error, safeUser, buildWelcomeMessage]);

  const platformChartData = useMemo(() => {
    if (!analytics?.count_per_platform?.length) return [];
    const total =
      analytics.count_per_platform.reduce((sum, item) => sum + item.count, 0) || 1;

    return analytics.count_per_platform.map((item) => ({
      ...item,
      percentage: Math.round((item.count / total) * 100)
    }));
  }, [analytics]);

  const trendChartData = useMemo(() => trends?.daily_average_score || [], [trends]);

  const topicChartData = useMemo(() => {
    if (!analytics?.average_score_per_topic?.length) return [];
    return [...analytics.average_score_per_topic]
      .sort((a, b) => b.average_score - a.average_score)
      .slice(0, 6);
  }, [analytics]);

  const studioRunTrendData = safeStudioUsageAnalytics.daily_runs || [];
  const studioRunValues = studioRunTrendData.map((item) => Number(item.total_runs || 0));
  const studioRunPath = useMemo(
    () => buildLinePath(studioRunValues, 420, 220, 24),
    [studioRunValues]
  );
  const studioRunPoints = useMemo(
    () => buildPoints(studioRunValues, 420, 220, 24),
    [studioRunValues]
  );
  const studioToolsData = useMemo(
    () => safeStudioUsageAnalytics.tools || [],
    [safeStudioUsageAnalytics]
  );

  const strongestPerTopic = useMemo(() => {
    const map = {};
    safeTopHooks.forEach((hook) => {
      if (!map[hook.topic] || hook.score > map[hook.topic].score) {
        map[hook.topic] = hook;
      }
    });
    return map;
  }, [safeTopHooks]);

  const allPlatformLabel = t("dashboard.filters.allPlatforms", "All");
  const allToolsLabel = t("dashboard.filters.allTools", "All");

  const platforms = useMemo(
    () => [
      allPlatformLabel,
      ...new Set(
        [...safeTopHooks, ...safeRecentHooks, ...safeFavoriteHooks, ...safeRecentStudioAssets]
          .map((h) => h.platform)
          .filter(Boolean)
      )
    ],
    [
      allPlatformLabel,
      safeFavoriteHooks,
      safeRecentHooks,
      safeRecentStudioAssets,
      safeTopHooks
    ]
  );

  const availableStudioTools = useMemo(() => {
    const map = {};

    safeRecentStudioAssets.forEach((asset) => {
      const meta = getToolMeta(asset.tool_type, asset.tool_label, asset.tool_badge, t);
      map[meta.toolType] = {
        toolType: meta.toolType,
        label: meta.label,
        badge: meta.badge
      };
    });

    safeTopToolTypes.forEach((item) => {
      const meta = getToolMeta(item.tool_type, item.tool_label, item.tool_badge, t);
      map[meta.toolType] = {
        toolType: meta.toolType,
        label: meta.label,
        badge: meta.badge
      };
    });

    studioToolsData.forEach((item) => {
      const meta = getToolMeta(item.tool_name, item.tool_label, item.tool_badge, t);
      map[meta.toolType] = {
        toolType: meta.toolType,
        label: meta.label,
        badge: meta.badge
      };
    });

    return Object.values(map).sort((a, b) => a.label.localeCompare(b.label));
  }, [safeRecentStudioAssets, safeTopToolTypes, studioToolsData, t]);

  const trendValues = trendChartData.map((item) => Number(item.avg_score));
  const trendPath = useMemo(
    () => buildLinePath(trendValues, 420, 220, 24),
    [trendValues]
  );
  const trendPoints = useMemo(
    () => buildPoints(trendValues, 420, 220, 24),
    [trendValues]
  );

  const filterSortSearchHooks = useCallback(
    (hooks) => {
      let filtered = [...hooks];

      if (platformFilter !== allPlatformLabel) {
        filtered = filtered.filter((h) => h.platform === platformFilter);
      }

      if (searchQuery.trim()) {
        filtered = filtered.filter(
          (h) =>
            (h.topic || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
            (h.content || "").toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      if (sortBy === "score") {
        filtered.sort((a, b) => (b.score || 0) - (a.score || 0));
      } else if (sortBy === "topic") {
        filtered.sort((a, b) => (a.topic || "").localeCompare(b.topic || ""));
      }

      return filtered;
    },
    [allPlatformLabel, platformFilter, searchQuery, sortBy]
  );

  const filterSortSearchAssets = useCallback(
    (assets) => {
      let filtered = [...assets];

      if (platformFilter !== allPlatformLabel) {
        filtered = filtered.filter((a) => !a.platform || a.platform === platformFilter);
      }

      if (toolFilter !== allToolsLabel) {
        filtered = filtered.filter(
          (a) => normalizeToolType(a.tool_type) === toolFilter
        );
      }

      if (searchQuery.trim()) {
        filtered = filtered.filter(
          (a) =>
            (a.topic || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
            (a.title || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
            (a.content || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
            (a.tool_type || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
            (a.tool_label || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
            (a.tool_badge || "").toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      if (sortBy === "topic") {
        filtered.sort((a, b) =>
          (a.topic || a.title || "").localeCompare(b.topic || b.title || "")
        );
      }

      return filtered;
    },
    [allPlatformLabel, allToolsLabel, platformFilter, searchQuery, sortBy, toolFilter]
  );

  const filterCombinedActivity = useCallback(
    (items) => {
      let filtered = [...items];

      if (platformFilter !== allPlatformLabel) {
        filtered = filtered.filter((item) =>
          item.type === "hook"
            ? item.platform === platformFilter
            : !item.platform || item.platform === platformFilter
        );
      }

      if (toolFilter !== allToolsLabel) {
        filtered = filtered.filter((item) =>
          item.type === "hook" ? true : normalizeToolType(item.tool_type) === toolFilter
        );
      }

      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter((item) => {
          if (item.type === "hook") {
            return (
              (item.topic || "").toLowerCase().includes(query) ||
              (item.content || "").toLowerCase().includes(query)
            );
          }

          return (
            (item.topic || "").toLowerCase().includes(query) ||
            (item.title || "").toLowerCase().includes(query) ||
            (item.content || "").toLowerCase().includes(query) ||
            (item.tool_type || "").toLowerCase().includes(query) ||
            (item.tool_label || "").toLowerCase().includes(query) ||
            (item.tool_badge || "").toLowerCase().includes(query)
          );
        });
      }

      if (sortBy === "score") {
        filtered.sort((a, b) => {
          if (a.type === "hook" && b.type === "hook") {
            return (b.score || 0) - (a.score || 0);
          }
          if (a.type === "hook") return -1;
          if (b.type === "hook") return 1;
          return 0;
        });
      } else if (sortBy === "topic") {
        filtered.sort((a, b) => {
          const aLabel = a.type === "hook" ? a.topic || "" : a.topic || a.title || "";
          const bLabel = b.type === "hook" ? b.topic || "" : b.topic || b.title || "";
          return aLabel.localeCompare(bLabel);
        });
      }

      return filtered;
    },
    [allPlatformLabel, allToolsLabel, platformFilter, searchQuery, sortBy, toolFilter]
  );

  const renderHookItem = (hook, isStrongest = false) => (
    <li
      key={`hook-${hook.id}`}
      style={{
        marginBottom: "16px",
        border: `1px solid ${colors.border}`,
        padding: "16px",
        borderRadius: "16px",
        position: "relative",
        listStyle: "none",
        background: colors.cardBgStrong,
        boxShadow: colors.shadowSoft,
        backdropFilter: isDark ? "blur(10px)" : "none"
      }}
    >
      {isStrongest && (
        <span
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            background: colors.bestBadgeBg,
            color: "#fff",
            padding: "5px 9px",
            borderRadius: "999px",
            fontSize: "12px",
            fontWeight: "600"
          }}
        >
          {t("dashboard.labels.best", "Best")}
        </span>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "12px",
          flexWrap: "wrap"
        }}
      >
        <div
          style={{
            paddingRight: isMobile ? "0" : "60px",
            color: colors.text,
            maxWidth: "100%"
          }}
        >
          <strong>{hook.topic}</strong> ({hook.platform}) - {t("dashboard.labels.score", "Score")}:{" "}
          {hook.score}
        </div>

        {hook.is_favorite && (
          <span
            style={{
              background: colors.favoriteBadgeBg,
              color: colors.favoriteBadgeText,
              padding: "5px 10px",
              borderRadius: "999px",
              fontSize: "12px",
              height: "fit-content",
              fontWeight: "600",
              border: isDark ? `1px solid ${colors.border}` : "none"
            }}
          >
            {t("dashboard.labels.favorite", "Favorite")}
          </span>
        )}
      </div>

      <p
        style={{
          marginTop: "10px",
          marginBottom: "8px",
          color: colors.textSoft,
          lineHeight: "1.6"
        }}
      >
        {hook.content}
      </p>
      {renderScoreBar(hook.score)}

      <div
        style={{
          display: "flex",
          gap: "8px",
          flexWrap: "wrap",
          marginTop: "14px"
        }}
      >
        <button
          onClick={() => copyHook(hook.content, hook.id)}
          style={{
            padding: "10px 14px",
            borderRadius: "10px",
            border: "none",
            backgroundColor: copiedHookId === hook.id ? "#16a34a" : "#2563eb",
            color: "#fff",
            cursor: "pointer",
            fontWeight: "600",
            flex: isMobile ? "1 1 140px" : "0 0 auto"
          }}
        >
          {copiedHookId === hook.id
            ? t("dashboard.actions.copied", "Copied!")
            : t("dashboard.actions.copyHook", "Copy Hook")}
        </button>

        <button
          onClick={() => regenerateHook(hook.id)}
          disabled={actionLoadingId === hook.id}
          style={{
            padding: "10px 14px",
            borderRadius: "10px",
            border: "none",
            backgroundColor: "#7c3aed",
            color: "#fff",
            cursor: actionLoadingId === hook.id ? "not-allowed" : "pointer",
            fontWeight: "600",
            opacity: actionLoadingId === hook.id ? 0.7 : 1,
            flex: isMobile ? "1 1 140px" : "0 0 auto"
          }}
        >
          {actionLoadingId === hook.id
            ? t("dashboard.actions.working", "Working...")
            : t("dashboard.actions.regenerate", "Regenerate")}
        </button>

        <button
          onClick={() => toggleFavorite(hook.id)}
          disabled={actionLoadingId === hook.id}
          style={{
            padding: "10px 14px",
            borderRadius: "10px",
            border: "none",
            backgroundColor: hook.is_favorite ? "#f59e0b" : "#64748b",
            color: "#fff",
            cursor: actionLoadingId === hook.id ? "not-allowed" : "pointer",
            fontWeight: "600",
            opacity: actionLoadingId === hook.id ? 0.7 : 1,
            flex: isMobile ? "1 1 140px" : "0 0 auto"
          }}
        >
          {actionLoadingId === hook.id
            ? t("dashboard.actions.saving", "Saving...")
            : hook.is_favorite
              ? t("dashboard.actions.unfavorite", "Unfavorite")
              : t("dashboard.actions.favorite", "Favorite")}
        </button>

        <button
          onClick={() => deleteHook(hook.id)}
          disabled={actionLoadingId === hook.id}
          style={{
            padding: "10px 14px",
            borderRadius: "10px",
            border: "none",
            backgroundColor: "#ef4444",
            color: "#fff",
            cursor: actionLoadingId === hook.id ? "not-allowed" : "pointer",
            fontWeight: "600",
            opacity: actionLoadingId === hook.id ? 0.7 : 1,
            flex: isMobile ? "1 1 140px" : "0 0 auto"
          }}
        >
          {actionLoadingId === hook.id
            ? t("dashboard.actions.working", "Working...")
            : t("dashboard.actions.delete", "Delete")}
        </button>
      </div>
    </li>
  );

  const renderStudioAssetItem = (asset) => (
    <li
      key={`asset-${asset.id}`}
      style={{
        marginBottom: "16px",
        border: `1px solid ${colors.border}`,
        padding: "16px",
        borderRadius: "16px",
        listStyle: "none",
        background: colors.cardBgStrong,
        boxShadow: colors.shadowSoft,
        backdropFilter: isDark ? "blur(10px)" : "none"
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "12px",
          flexWrap: "wrap"
        }}
      >
        <div style={{ color: colors.text, flex: 1, minWidth: 0 }}>
          <strong>{asset.title}</strong>

          <div
            style={{
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
              alignItems: "center",
              marginTop: "8px"
            }}
          >
            <span
              style={{
                ...getToolBadgeStyle(asset.tool_type),
                display: "inline-block",
                padding: "5px 10px",
                borderRadius: "999px",
                fontSize: "12px",
                fontWeight: "700"
              }}
            >
              {asset.tool_badge}
            </span>

            <span
              style={{
                background: colors.cardSubtle,
                color: colors.textSoft,
                border: `1px solid ${colors.border}`,
                padding: "5px 10px",
                borderRadius: "999px",
                fontSize: "12px",
                fontWeight: "600"
              }}
            >
              {asset.tool_label}
            </span>

            {asset.platform ? (
              <span
                style={{
                  background: colors.cardSubtle,
                  color: colors.textSoft,
                  border: `1px solid ${colors.border}`,
                  padding: "5px 10px",
                  borderRadius: "999px",
                  fontSize: "12px",
                  fontWeight: "600"
                }}
              >
                {asset.platform}
              </span>
            ) : null}

            {asset.topic ? (
              <span
                style={{
                  background: colors.cardSubtle,
                  color: colors.textMuted,
                  border: `1px solid ${colors.border}`,
                  padding: "5px 10px",
                  borderRadius: "999px",
                  fontSize: "12px",
                  fontWeight: "500"
                }}
              >
                {asset.topic}
              </span>
            ) : null}
          </div>

          <div style={{ marginTop: "8px", color: colors.textMuted, fontSize: "13px" }}>
            {t("dashboard.labels.toolType", "Tool type")}: {asset.tool_type}
          </div>
        </div>
      </div>

      <p
        style={{
          marginTop: "10px",
          marginBottom: "8px",
          color: colors.textSoft,
          lineHeight: "1.6",
          whiteSpace: "pre-wrap"
        }}
      >
        {asset.content}
      </p>

      <div
        style={{
          display: "flex",
          gap: "8px",
          flexWrap: "wrap",
          marginTop: "14px"
        }}
      >
        <button
          onClick={() => copyHook(asset.content, `asset-${asset.id}`)}
          style={{
            padding: "10px 14px",
            borderRadius: "10px",
            border: "none",
            backgroundColor:
              copiedHookId === `asset-${asset.id}` ? "#16a34a" : colors.buttonDarkBg,
            color: colors.buttonDarkText,
            cursor: "pointer",
            fontWeight: "600",
            flex: isMobile ? "1 1 140px" : "0 0 auto"
          }}
        >
          {copiedHookId === `asset-${asset.id}`
            ? t("dashboard.actions.copied", "Copied!")
            : t("dashboard.actions.copyAsset", "Copy Asset")}
        </button>

        <button
          onClick={() => deleteStudioAsset(asset.id)}
          disabled={actionLoadingId === `asset-${asset.id}`}
          style={{
            padding: "10px 14px",
            borderRadius: "10px",
            border: "none",
            backgroundColor: "#ef4444",
            color: "#fff",
            cursor:
              actionLoadingId === `asset-${asset.id}` ? "not-allowed" : "pointer",
            fontWeight: "600",
            opacity: actionLoadingId === `asset-${asset.id}` ? 0.7 : 1,
            flex: isMobile ? "1 1 140px" : "0 0 auto"
          }}
        >
          {actionLoadingId === `asset-${asset.id}`
            ? t("dashboard.actions.working", "Working...")
            : t("dashboard.actions.delete", "Delete")}
        </button>
      </div>
    </li>
  );

  if (error) {
    return (
      <div
        style={{
          padding: "40px",
          minHeight: "100vh",
          background: colors.pageBg,
          color: colors.alertDangerText
        }}
      >
        <h2>{error}</h2>
      </div>
    );
  }

  if (!stats || !analytics || !trends) {
  return (
    <DashboardSkeleton
      isDark={isDark}
      isMobile={isMobile}
      colors={colors}
    />
  );
}

  const metrics = safeMetrics;
  const top_hooks = safeTopHooks;
  const recent_hooks = safeRecentHooks;
  const favorite_hooks = safeFavoriteHooks;
  const recent_studio_assets = safeRecentStudioAssets;
  const top_tool_types = safeTopToolTypes;
  const combined_activity = safeCombinedActivity;
  const usage = safeUsage;
  const usage_messages = safeUsageMessages;
  const founder_overview = safeFounderOverview;
  const user = safeUser;
  const studioUsageAnalytics = safeStudioUsageAnalytics;

  const isPro = usage?.plan === "pro";
  const isAdmin = Boolean(user?.is_admin);
  const hasAnyContent =
    top_hooks.length ||
    recent_hooks.length ||
    recent_studio_assets.length ||
    favorite_hooks.length;

  const filteredFavoriteHooks = filterSortSearchHooks(favorite_hooks);
  const filteredTopHooks = filterSortSearchHooks(top_hooks);
  const filteredRecentHooks = filterSortSearchHooks(recent_hooks);
  const filteredRecentStudioAssets = filterSortSearchAssets(recent_studio_assets);
  const filteredCombinedActivity = filterCombinedActivity(combined_activity);

  const lockedAnalyticsCard = (title, description) => (
    <button
      onClick={() => showUpgradePrompt(title)}
      style={{
        ...cardStyle,
        textAlign: "left",
        cursor: "pointer",
        border: `1px dashed ${colors.lockedBorder}`,
        background: colors.lockedBg
      }}
    >
      <h2 style={{ marginTop: 0, color: colors.text }}>
        {title}
        {featureBadge(t("dashboard.labels.pro", "PRO"))}
      </h2>
      <p style={{ color: colors.textMuted, lineHeight: "1.7", marginBottom: 0 }}>
        {description}
      </p>
      <div style={{ marginTop: "14px", color: colors.lockedText, fontWeight: "700" }}>
        {t("dashboard.upgradePrompt.clickToUnlock", "Click to unlock with Pro")}
      </div>
    </button>
  );

  const isHardLimit =
    usage_messages?.reached_generation_limit || usage_messages?.reached_saved_limit;
  const isNearLimit =
    usage_messages?.near_generation_limit || usage_messages?.near_saved_limit;

  return (
   <div
    style={{
      padding: isMobile ? "24px 14px" : "32px 20px",
      background: colors.pageBg,
      backgroundImage: colors.pageGradient,
      minHeight: "100vh"
    }}
  >
    <div
      style={{
        position: "fixed",
        top: "18px",
        left: "50%",
        transform: welcomePopup.visible
          ? "translateX(-50%) translateY(0)"
          : "translateX(-50%) translateY(-18px)",
        opacity: welcomePopup.visible ? 1 : 0,
        pointerEvents: "none",
        zIndex: 9999,
        transition: "all 0.35s ease",
        width: isMobile ? "calc(100% - 24px)" : "min(520px, calc(100% - 32px))"
      }}
    >
      <div
        style={{
          background: isDark ? "rgba(15,23,42,0.96)" : "rgba(255,255,255,0.96)",
          border: `1px solid ${isDark ? "rgba(148,163,184,0.24)" : "#dbeafe"}`,
          boxShadow: isDark
            ? "0 16px 40px rgba(2, 6, 23, 0.5)"
            : "0 14px 34px rgba(15, 23, 42, 0.12)",
          borderRadius: "18px",
          padding: isMobile ? "14px 16px" : "16px 18px",
          backdropFilter: "blur(16px)"
        }}
      >
        <div
          style={{
            color: colors.text,
            fontWeight: "800",
            fontSize: isMobile ? "14px" : "15px",
            marginBottom: "4px",
            lineHeight: "1.4"
          }}
        >
          {welcomePopup.title}
        </div>

        <div
          style={{
            color: colors.textMuted,
            fontSize: isMobile ? "13px" : "14px",
            lineHeight: "1.5"
          }}
        >
          {welcomePopup.message}
        </div>
      </div>
    </div>

    <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <h1
          style={{
            marginBottom: "8px",
            color: colors.text,
            fontSize: isMobile ? "28px" : "34px"
          }}
        >
          {t("dashboard.title", "Dashboard")}
        </h1>
        <p style={{ color: colors.textMuted, marginTop: 0, lineHeight: "1.6" }}>
          {t(
            "dashboard.subtitle",
            "Track your hook performance, Studio activity, trends, and top-performing content."
          )}
        </p>

        <div
          style={{
            marginTop: "22px",
            marginBottom: "10px",
            background: colors.rewardsBg,
            border: `1px solid ${colors.rewardsBorder}`,
            borderRadius: "18px",
            padding: isMobile ? "18px" : "22px",
            boxShadow: colors.shadow,
            backdropFilter: isDark ? "blur(14px)" : "none"
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "14px",
              alignItems: isMobile ? "flex-start" : "center",
              flexDirection: isMobile ? "column" : "row",
              marginBottom: "16px"
            }}
          >
            <div>
              <h2 style={{ margin: 0, color: colors.text }}>
                {t("dashboard.rewards.title", "Hookora Creator Rewards")}
              </h2>
              <p style={{ margin: "8px 0 0", color: colors.textMuted, lineHeight: "1.7" }}>
                {t(
                  "dashboard.rewards.subtitle",
                  "Keep an eye on your Reward Credits and referral growth from one place."
                )}
              </p>
            </div>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                background: colors.rewardsAccentBg,
                border: `1px solid ${colors.rewardsAccentBorder}`,
                color: colors.rewardsAccentText,
                padding: "10px 14px",
                borderRadius: "999px",
                fontWeight: "700",
                fontSize: "13px"
              }}
            >
              {t("dashboard.rewards.rewardCredits", "Reward Credits")}:{" "}
              {formatCurrency(rewards?.reward_credits_balance_naira || 0)}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1fr",
              gap: "16px"
            }}
          >
            <div
              style={{
                background: colors.rewardsSoftBg,
                border: `1px solid ${colors.rewardsSoftBorder}`,
                borderRadius: "16px",
                padding: "16px"
              }}
            >
              <div style={{ color: colors.text, fontWeight: "700", marginBottom: "12px" }}>
                {t("dashboard.rewards.quickShare", "Referral quick share")}
              </div>

              <div style={{ display: "grid", gap: "12px" }}>
                <div>
                  <div
                    style={{
                      color: colors.textMuted,
                      fontSize: "12px",
                      marginBottom: "6px",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      fontWeight: "700"
                    }}
                  >
                    {t("dashboard.rewards.referralCode", "Referral Code")}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      flexDirection: isMobile ? "column" : "row"
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        background: colors.cardBgStrong,
                        border: `1px solid ${colors.border}`,
                        borderRadius: "12px",
                        padding: "12px 14px",
                        color: colors.text,
                        fontWeight: "700",
                        letterSpacing: "0.04em"
                      }}
                    >
                      {rewards?.referral_code || "—"}
                    </div>

                    <button
                      onClick={() =>
                        rewards?.referral_code &&
                        copyRewardText(rewards.referral_code, "code")
                      }
                      disabled={!rewards?.referral_code}
                      style={{
                        padding: "12px 16px",
                        borderRadius: "12px",
                        border: "none",
                        background: colors.successButtonBg,
                        color: "#fff",
                        fontWeight: "700",
                        cursor: rewards?.referral_code ? "pointer" : "not-allowed",
                        opacity: rewards?.referral_code ? 1 : 0.6
                      }}
                    >
                      {copiedRewardField === "code"
                        ? t("dashboard.actions.copied", "Copied")
                        : t("dashboard.actions.copyCode", "Copy Code")}
                    </button>
                  </div>
                </div>

                <div>
                  <div
                    style={{
                      color: colors.textMuted,
                      fontSize: "12px",
                      marginBottom: "6px",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      fontWeight: "700"
                    }}
                  >
                    {t("dashboard.rewards.referralLink", "Referral Link")}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      flexDirection: isMobile ? "column" : "row"
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        background: colors.cardBgStrong,
                        border: `1px solid ${colors.border}`,
                        borderRadius: "12px",
                        padding: "12px 14px",
                        color: colors.textSoft,
                        fontSize: "13px",
                        overflowWrap: "anywhere"
                      }}
                    >
                      {rewards?.referral_link || "—"}
                    </div>

                    <button
                      onClick={() =>
                        rewards?.referral_link &&
                        copyRewardText(rewards.referral_link, "link")
                      }
                      disabled={!rewards?.referral_link}
                      style={{
                        padding: "12px 16px",
                        borderRadius: "12px",
                        border: "none",
                        background: colors.buttonDarkBg,
                        color: colors.buttonDarkText,
                        fontWeight: "700",
                        cursor: rewards?.referral_link ? "pointer" : "not-allowed",
                        opacity: rewards?.referral_link ? 1 : 0.6
                      }}
                    >
                      {copiedRewardField === "link"
                        ? t("dashboard.actions.copied", "Copied")
                        : t("dashboard.actions.copyLink", "Copy Link")}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div
              style={{
                background: colors.rewardsSoftBg,
                border: `1px solid ${colors.rewardsSoftBorder}`,
                borderRadius: "16px",
                padding: "16px"
              }}
            >
              <div style={{ color: colors.text, fontWeight: "700", marginBottom: "12px" }}>
                {t("dashboard.rewards.snapshot", "Rewards snapshot")}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px"
                }}
              >
                <div
                  style={{
                    background: colors.rewardsAccentBg,
                    border: `1px solid ${colors.rewardsAccentBorder}`,
                    borderRadius: "14px",
                    padding: "14px"
                  }}
                >
                  <div style={{ color: colors.textMuted, fontSize: "12px", marginBottom: "6px" }}>
                    {t("dashboard.rewards.rewardCredits", "Reward Credits")}
                  </div>
                  <div
                    style={{
                      color: colors.rewardsAccentText,
                      fontWeight: "800",
                      fontSize: "20px"
                    }}
                  >
                    {formatCurrency(rewards?.reward_credits_balance_naira || 0)}
                  </div>
                </div>

                <div
                  style={{
                    background: colors.walletBg,
                    border: `1px solid ${colors.walletBorder}`,
                    borderRadius: "14px",
                    padding: "14px"
                  }}
                >
                  <div style={{ color: colors.textMuted, fontSize: "12px", marginBottom: "6px" }}>
                    {t("dashboard.rewards.walletBalance", "Wallet Balance")}
                  </div>
                  <div
                    style={{
                      color: colors.walletText,
                      fontWeight: "800",
                      fontSize: "20px"
                    }}
                  >
                    {formatCurrency(user?.wallet_balance_naira || 0)}
                  </div>
                </div>

                <div
                  style={{
                    background: colors.cardSubtle,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "14px",
                    padding: "14px"
                  }}
                >
                  <div style={{ color: colors.textMuted, fontSize: "12px", marginBottom: "6px" }}>
                    {t("dashboard.rewards.totalReferrals", "Total Referrals")}
                  </div>
                  <div style={{ color: colors.text, fontWeight: "700", fontSize: "18px" }}>
                    {rewards?.total_referrals ?? 0}
                  </div>
                </div>

                <div
                  style={{
                    background: colors.cardSubtle,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "14px",
                    padding: "14px"
                  }}
                >
                  <div style={{ color: colors.textMuted, fontSize: "12px", marginBottom: "6px" }}>
                    {t("dashboard.rewards.verifiedReferrals", "Verified Referrals")}
                  </div>
                  <div style={{ color: colors.text, fontWeight: "700", fontSize: "18px" }}>
                    {rewards?.verified_referrals ?? 0}
                  </div>
                </div>

                <div
                  style={{
                    background: colors.cardSubtle,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "14px",
                    padding: "14px",
                    gridColumn: "1 / -1"
                  }}
                >
                  <div style={{ color: colors.textMuted, fontSize: "12px", marginBottom: "6px" }}>
                    {t("dashboard.rewards.paidReferrals", "Paid Referrals")}
                  </div>
                  <div style={{ color: colors.text, fontWeight: "700", fontSize: "18px" }}>
                    {rewards?.paid_referrals ?? 0}
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  flexWrap: "wrap",
                  marginTop: "14px"
                }}
              >
                <button
                  onClick={() => navigate("/app/pricing")}
                  style={{
                    padding: "10px 14px",
                    borderRadius: "10px",
                    border: "none",
                    background: colors.successButtonBg,
                    color: "#ffffff",
                    fontWeight: "700",
                    cursor: "pointer"
                  }}
                >
                  {t("dashboard.actions.openBilling", "Open Billing")}
                </button>

                <button
                  onClick={() => navigate("/register")}
                  style={{
                    padding: "10px 14px",
                    borderRadius: "10px",
                    border: "none",
                    background: colors.secondaryButtonBg,
                    color: colors.secondaryButtonText,
                    fontWeight: "700",
                    cursor: "pointer"
                  }}
                >
                  {t("dashboard.actions.viewSignupFlow", "View Signup Flow")}
                </button>
              </div>
            </div>
          </div>
        </div>

        {isAdmin && founder_overview && (
          <div
            style={{
              marginTop: "24px",
              marginBottom: "12px",
              padding: isMobile ? "18px" : "22px",
              border: `1px solid ${colors.founderBorder}`,
              borderRadius: "18px",
              background: colors.founderBg,
              boxShadow: colors.shadow
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "12px",
                flexWrap: "wrap",
                alignItems: "center",
                marginBottom: "16px"
              }}
            >
              <div>
                <h2 style={{ margin: 0, color: colors.text }}>
                  {t("dashboard.founder.title", "Founder Overview")}
                </h2>
                <p style={{ margin: "6px 0 0", color: colors.textMuted, lineHeight: "1.6" }}>
                  {t(
                    "dashboard.founder.subtitle",
                    "Company-level growth and conversion snapshot."
                  )}
                </p>
              </div>

              <Link
                to="/admin/milestone-watch"
                style={{
                  textDecoration: "none",
                  background: colors.buttonDarkBg,
                  color: colors.buttonDarkText,
                  padding: "10px 14px",
                  borderRadius: "10px",
                  fontWeight: "700"
                }}
              >
                {t("dashboard.actions.openMilestoneWatch", "Open Milestone Watch")}
              </Link>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "14px"
              }}
            >
              <MiniFounderCard
                label={t("dashboard.founder.totalUsers", "Total Users")}
                value={founder_overview.total_users}
                colors={colors}
              />
              <MiniFounderCard
                label={t("dashboard.founder.activeUsers", "Active Users")}
                value={founder_overview.active_users}
                colors={colors}
              />
              <MiniFounderCard
                label={t("dashboard.founder.paidUsers", "Paid Users")}
                value={founder_overview.paid_users}
                colors={colors}
              />
              <MiniFounderCard
                label={t("dashboard.founder.freeUsers", "Free Users")}
                value={founder_overview.free_users}
                colors={colors}
              />
              <MiniFounderCard
                label={t("dashboard.founder.creatorUsers", "Creator Users")}
                value={founder_overview.creator_users}
                colors={colors}
              />
              <MiniFounderCard
                label={t("dashboard.founder.proUsers", "Pro Users")}
                value={founder_overview.pro_users}
                colors={colors}
              />
              <MiniFounderCard
                label={t("dashboard.founder.newUsers7d", "New Users (7d)")}
                value={founder_overview.new_users_7d}
                colors={colors}
              />
              <MiniFounderCard
                label={t("dashboard.founder.newUsers30d", "New Users (30d)")}
                value={founder_overview.new_users_30d}
                colors={colors}
              />
              <MiniFounderCard
                label={t("dashboard.founder.signupToPaid", "Signup → Paid")}
                value={`${founder_overview.signup_to_paid_conversion_rate}%`}
                colors={colors}
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1.1fr 0.9fr",
                gap: "16px",
                marginTop: "18px"
              }}
            >
              <div
                style={{
                  background: colors.founderPanelBg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "16px",
                  padding: "16px",
                  boxShadow: colors.shadowSoft
                }}
              >
                <h3 style={{ marginTop: 0, color: colors.text }}>
                  {t("dashboard.founder.weeklyFocus", "Weekly Founder Focus")}
                </h3>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: "18px",
                    color: colors.textSoft,
                    lineHeight: "1.8"
                  }}
                >
                  {(founder_overview.weekly_focus || []).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>

              <div
                style={{
                  background: colors.founderPanelBg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "16px",
                  padding: "16px",
                  boxShadow: colors.shadowSoft
                }}
              >
                <h3 style={{ marginTop: 0, color: colors.text }}>
                  {t("dashboard.founder.recentSignups", "Recent Signups")}
                </h3>
                {founder_overview.recent_signups?.length ? (
                  <div style={{ display: "grid", gap: "10px" }}>
                    {founder_overview.recent_signups.slice(0, 5).map((signup) => (
                      <div
                        key={signup.id}
                        style={{
                          padding: "10px 12px",
                          borderRadius: "12px",
                          background: colors.founderPanelSubtleBg,
                          border: `1px solid ${colors.border}`
                        }}
                      >
                        <div style={{ fontWeight: "700", color: colors.text }}>
                          {signup.username}
                        </div>
                        <div
                          style={{
                            color: colors.textMuted,
                            fontSize: "13px",
                            marginTop: "4px"
                          }}
                        >
                          {signup.email}
                        </div>
                        <div
                          style={{
                            color: colors.textSoft,
                            fontSize: "13px",
                            marginTop: "6px"
                          }}
                        >
                          {t("dashboard.labels.plan", "Plan")}: {signup.plan || t("dashboard.plan.freeLowercase", "free")}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ margin: 0, color: colors.textMuted }}>
                    {t("dashboard.founder.noSignupData", "No signup data yet.")}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {usage && (
          <div
            style={{
              marginTop: "20px",
              marginBottom: "8px",
              background: isHardLimit
                ? colors.alertDangerBg
                : isNearLimit
                  ? colors.alertWarningBg
                  : usage.plan === "free"
                    ? colors.alertWarningBg
                    : colors.alertInfoBg,
              border: `1px solid ${
                isHardLimit
                  ? colors.alertDangerBorder
                  : isNearLimit
                    ? colors.alertWarningBorder
                    : usage.plan === "free"
                      ? colors.alertWarningBorder
                      : colors.alertInfoBorder
              }`,
              borderRadius: "16px",
              padding: "16px",
              boxShadow: colors.shadowSoft
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
                marginBottom: "6px"
              }}
            >
              <div style={{ fontWeight: "700", color: colors.text }}>
                {t("dashboard.usage.title", "Plan Usage")}:
              </div>
              <span
                style={{
                  ...getPlanBadgeStyle(usage.plan),
                  fontSize: "12px",
                  fontWeight: "700",
                  padding: "7px 11px",
                  borderRadius: "999px"
                }}
              >
                {formatPlanLabel(usage.plan)}
              </span>
            </div>

            <div style={{ color: colors.textSoft, fontSize: "14px" }}>
              {t("dashboard.usage.monthlyGenerationsUsed", "Monthly generations used")}:{" "}
              {usage.monthly_generation_count} / {usage.monthly_generation_limit}
            </div>
            <div style={{ color: colors.textSoft, fontSize: "14px" }}>
              {t("dashboard.usage.remainingThisMonth", "Remaining this month")}:{" "}
              {usage.monthly_generations_remaining}
            </div>
            {usage.saved_hook_limit !== null && (
              <div style={{ color: colors.textSoft, fontSize: "14px" }}>
                {t("dashboard.usage.savedHooks", "Saved hooks")}:{" "}
                {usage.saved_hooks_count} / {usage.saved_hook_limit}
              </div>
            )}

            {usage_messages?.messages?.length > 0 && (
              <div
                style={{
                  marginTop: "10px",
                  color: isHardLimit ? colors.alertDangerText : colors.alertWarningText,
                  fontWeight: "600"
                }}
              >
                {usage_messages.messages.map((message) => (
                  <div key={message}>{message}</div>
                ))}
              </div>
            )}

            {usage.plan !== "pro" && (
              <div style={{ marginTop: "10px" }}>
                <Link
                  to="/app/pricing"
                  style={{
                    display: "inline-block",
                    textDecoration: "none",
                    background: colors.buttonDarkBg,
                    color: colors.buttonDarkText,
                    padding: "10px 14px",
                    borderRadius: "10px",
                    fontWeight: "700"
                  }}
                >
                  {t("dashboard.actions.upgradePlan", "Upgrade Plan")}
                </Link>
              </div>
            )}
          </div>
        )}

        {upgradePrompt && (
          <div
            style={{
              marginTop: "12px",
              marginBottom: "16px",
              background: colors.lockedBg,
              border: `1px solid ${colors.lockedBorder}`,
              borderRadius: "14px",
              padding: "14px 16px",
              color: colors.lockedText,
              display: "flex",
              justifyContent: "space-between",
              gap: "12px",
              flexWrap: "wrap",
              alignItems: "center",
              boxShadow: colors.shadowSoft
            }}
          >
            <span>{upgradePrompt}</span>

            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button
                onClick={() => setUpgradePrompt("")}
                style={{
                  padding: "10px 12px",
                  borderRadius: "10px",
                  border: `1px solid ${colors.lockedBorder}`,
                  background: colors.cardBgStrong,
                  color: colors.lockedText,
                  fontWeight: "700",
                  cursor: "pointer"
                }}
              >
                {t("dashboard.actions.dismiss", "Dismiss")}
              </button>

              <button
                onClick={() => navigate("/app/pricing")}
                style={{
                  padding: "10px 12px",
                  borderRadius: "10px",
                  border: "none",
                  background: colors.buttonDarkBg,
                  color: colors.buttonDarkText,
                  fontWeight: "700",
                  cursor: "pointer"
                }}
              >
                {t("dashboard.actions.seeProPlan", "See Pro Plan")}
              </button>
            </div>
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "16px",
            marginTop: "24px"
          }}
        >
          <div style={cardStyle}>
            <h3 style={{ marginTop: 0, color: colors.textSoft }}>
              {t("dashboard.metrics.totalHooks", "Total Hooks")}
            </h3>
            <p
              style={{
                fontSize: "30px",
                fontWeight: "700",
                margin: "8px 0 0",
                color: colors.text
              }}
            >
              {metrics.total_hooks}
            </p>
          </div>

          <div style={cardStyle}>
            <h3 style={{ marginTop: 0, color: colors.textSoft }}>
              {t("dashboard.metrics.studioAssets", "Studio Assets")}
            </h3>
            <p
              style={{
                fontSize: "30px",
                fontWeight: "700",
                margin: "8px 0 0",
                color: colors.text
              }}
            >
              {metrics.total_studio_assets || 0}
            </p>
          </div>

          <div style={cardStyle}>
            <h3 style={{ marginTop: 0, color: colors.textSoft }}>
              {t("dashboard.metrics.totalSavedItems", "Total Saved Items")}
            </h3>
            <p
              style={{
                fontSize: "30px",
                fontWeight: "700",
                margin: "8px 0 0",
                color: colors.text
              }}
            >
              {metrics.total_saved_items || 0}
            </p>
          </div>

          <div style={cardStyle}>
            <h3 style={{ marginTop: 0, color: colors.textSoft }}>
              {t("dashboard.metrics.studioRuns", "Studio Runs")}
            </h3>
            <p
              style={{
                fontSize: "30px",
                fontWeight: "700",
                margin: "8px 0 0",
                color: colors.text
              }}
            >
              {metrics.total_studio_runs || 0}
            </p>
            <div style={{ marginTop: "10px", color: colors.textMuted, fontSize: "13px" }}>
              {t("dashboard.metrics.actualTrackedToolRuns", "Actual tracked tool runs")}
            </div>
          </div>

          <div
            style={{
              ...cardStyle,
              ...(isPro
                ? {}
                : {
                    border: `1px dashed ${colors.lockedBorder}`,
                    background: colors.lockedBg,
                    cursor: "pointer"
                  })
            }}
            onClick={
              !isPro
                ? () =>
                    showUpgradePrompt(
                      t("dashboard.pro.averageScoreInsights", "Average Score Insights")
                    )
                : undefined
            }
          >
            <h3 style={{ marginTop: 0, color: colors.textSoft }}>
              {t("dashboard.metrics.averageScore", "Average Score")}
              {featureBadge(t("dashboard.labels.pro", "PRO"))}
            </h3>
            <p
              style={{
                fontSize: "30px",
                fontWeight: "700",
                margin: "8px 0 0",
                color: colors.text
              }}
            >
              {metrics.average_score.toFixed(2)}
            </p>
            {!isPro && (
              <div
                style={{
                  marginTop: "10px",
                  color: colors.lockedText,
                  fontWeight: "700",
                  fontSize: "13px"
                }}
              >
                {t("dashboard.upgradePrompt.clickForPro", "Click for Pro analytics upgrade")}
              </div>
            )}
          </div>

          <div style={cardStyle}>
            <h3 style={{ marginTop: 0, color: colors.textSoft }}>
              {t("dashboard.metrics.favoriteHooks", "Favorite Hooks")}
            </h3>
            <p
              style={{
                fontSize: "30px",
                fontWeight: "700",
                margin: "8px 0 0",
                color: colors.text
              }}
            >
              {metrics.favorite_hooks}
            </p>
          </div>

          <div
            style={{
              ...cardStyle,
              ...(isPro
                ? {}
                : {
                    border: `1px dashed ${colors.lockedBorder}`,
                    background: colors.lockedBg,
                    cursor: "pointer"
                  })
            }}
            onClick={
              !isPro
                ? () =>
                    showUpgradePrompt(
                      t("dashboard.pro.topHookScoreAnalytics", "Top Hook Score Analytics")
                    )
                : undefined
            }
          >
            <h3 style={{ marginTop: 0, color: colors.textSoft }}>
              {t("dashboard.metrics.topHookScore", "Top Hook Score")}
              {featureBadge(t("dashboard.labels.pro", "PRO"))}
            </h3>
            <p
              style={{
                fontSize: "30px",
                fontWeight: "700",
                margin: "8px 0 0",
                color: colors.text
              }}
            >
              {top_hooks.length ? top_hooks[0].score : 0}
            </p>
            {!isPro && (
              <div
                style={{
                  marginTop: "10px",
                  color: colors.lockedText,
                  fontWeight: "700",
                  fontSize: "13px"
                }}
              >
                {t("dashboard.upgradePrompt.clickForPro", "Click for Pro analytics upgrade")}
              </div>
            )}
          </div>

          <div style={cardStyle}>
            <h3 style={{ marginTop: 0, color: colors.textSoft }}>
              {t("dashboard.metrics.successRate", "Success Rate")}
            </h3>
            <p
              style={{
                fontSize: "30px",
                fontWeight: "700",
                margin: "8px 0 0",
                color: colors.text
              }}
            >
              {studioUsageAnalytics.success_rate || 0}%
            </p>
            <div style={{ marginTop: "10px", color: colors.textMuted, fontSize: "13px" }}>
              {t("dashboard.metrics.studioToolRunSuccess", "Studio tool run success")}
            </div>
          </div>
        </div>

        {availableStudioTools.length > 0 && (
          <div style={{ ...cardStyle, marginTop: "18px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "12px",
                flexWrap: "wrap",
                alignItems: "center",
                marginBottom: "12px"
              }}
            >
              <h2 style={{ margin: 0, color: colors.text }}>
                {t("dashboard.studioSnapshot.title", "Studio Tool Snapshot")}
              </h2>
              <span style={{ color: colors.textMuted, fontSize: "14px" }}>
                {t(
                  "dashboard.studioSnapshot.subtitle",
                  "Includes Hook Angles, CTA Generator, AI Humanizer, and other Studio tools"
                )}
              </span>
            </div>

            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              {availableStudioTools.map((tool) => {
                const savedCount = metrics.studio_tool_counts?.[tool.toolType] || 0;
                const runCount =
                  studioToolsData.find(
                    (item) => normalizeToolType(item.tool_name) === tool.toolType
                  )?.total_runs || 0;

                return (
                  <span
                    key={tool.toolType}
                    style={{
                      ...getToolBadgeStyle(tool.toolType),
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "8px 12px",
                      borderRadius: "999px",
                      fontSize: "12px",
                      fontWeight: "700"
                    }}
                  >
                    <span>{tool.label}</span>
                    <span
                      style={{
                        background: colors.cardBgStrong,
                        color: colors.textSoft,
                        border: `1px solid ${colors.border}`,
                        padding: "2px 8px",
                        borderRadius: "999px"
                      }}
                    >
                      {savedCount} {t("dashboard.labels.saved", "saved")}
                    </span>
                    <span
                      style={{
                        background: colors.cardBgStrong,
                        color: colors.textMuted,
                        border: `1px solid ${colors.border}`,
                        padding: "2px 8px",
                        borderRadius: "999px"
                      }}
                    >
                      {runCount} {t("dashboard.labels.runs", "runs")}
                    </span>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: "16px",
            marginTop: "24px"
          }}
        >
          <div style={cardStyle}>
            <h2 style={{ marginTop: 0, color: colors.text }}>
              {t("dashboard.charts.platformDistribution", "Platform Distribution")}
            </h2>
            {platformChartData.length ? (
              <div>
                {platformChartData.map((item) => (
                  <div key={item.platform} style={{ marginBottom: "14px" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "6px",
                        color: colors.textSoft,
                        gap: "10px",
                        flexWrap: "wrap"
                      }}
                    >
                      <span>{item.platform}</span>
                      <span>
                        {item.count} {t("dashboard.labels.hooks", "hooks")} • {item.percentage}%
                      </span>
                    </div>
                    <div
                      style={{
                        height: "12px",
                        background: colors.trackBg,
                        borderRadius: "999px",
                        overflow: "hidden"
                      }}
                    >
                      <div
                        style={{
                          width: `${item.percentage}%`,
                          height: "100%",
                          background: "#2563eb",
                          borderRadius: "999px"
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={secondaryTextStyle}>
                {t("dashboard.empty.noPlatformAnalytics", "No platform analytics yet.")}
              </p>
            )}
          </div>

          <div style={cardStyle}>
            <h2 style={{ marginTop: 0, color: colors.text }}>
              {t("dashboard.charts.studioToolUsage", "Studio Tool Usage")}
            </h2>
            {studioToolsData.length ? (
              <div>
                {studioToolsData.map((item) => {
                  const maxRuns = Math.max(
                    ...studioToolsData.map((tool) => tool.total_runs || 0),
                    1
                  );
                  const width = Math.max(
                    8,
                    Math.round(((item.total_runs || 0) / maxRuns) * 100)
                  );

                  return (
                    <div key={item.tool_name} style={{ marginBottom: "14px" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "6px",
                          color: colors.textSoft,
                          gap: "10px",
                          flexWrap: "wrap"
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            flexWrap: "wrap"
                          }}
                        >
                          <span>{item.tool_label || formatToolName(item.tool_name)}</span>
                          <span
                            style={{
                              ...getToolBadgeStyle(item.tool_name),
                              padding: "3px 8px",
                              borderRadius: "999px",
                              fontSize: "11px",
                              fontWeight: "700"
                            }}
                          >
                            {item.tool_badge ||
                              getToolMeta(item.tool_name, null, null, t).badge}
                          </span>
                        </div>
                        <span>
                          {item.total_runs} {t("dashboard.labels.runs", "runs")}
                        </span>
                      </div>
                      <div
                        style={{
                          height: "12px",
                          background: colors.trackBg,
                          borderRadius: "999px",
                          overflow: "hidden"
                        }}
                      >
                        <div
                          style={{
                            width: `${width}%`,
                            height: "100%",
                            background: "#7c3aed",
                            borderRadius: "999px"
                          }}
                        />
                      </div>
                      <div
                        style={{
                          marginTop: "6px",
                          color: colors.textMuted,
                          fontSize: "12px",
                          lineHeight: "1.5"
                        }}
                      >
                        {t("dashboard.labels.success", "Success")}: {item.successful_runs} •{" "}
                        {t("dashboard.labels.failed", "Failed")}: {item.failed_runs} •{" "}
                        {t("dashboard.labels.avgTime", "Avg time")}:{" "}
                        {item.avg_generation_ms || 0}ms
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : top_tool_types.length ? (
              <div>
                {top_tool_types.map((item) => (
                  <div key={item.tool_type} style={{ marginBottom: "14px" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "6px",
                        color: colors.textSoft,
                        gap: "10px",
                        flexWrap: "wrap"
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          flexWrap: "wrap"
                        }}
                      >
                        <span>{item.tool_label || formatToolName(item.tool_type)}</span>
                        <span
                          style={{
                            ...getToolBadgeStyle(item.tool_type),
                            padding: "3px 8px",
                            borderRadius: "999px",
                            fontSize: "11px",
                            fontWeight: "700"
                          }}
                        >
                          {item.tool_badge ||
                            getToolMeta(item.tool_type, null, null, t).badge}
                        </span>
                      </div>
                      <span>
                        {item.count} {t("dashboard.labels.saved", "saved")}
                      </span>
                    </div>
                    <div
                      style={{
                        height: "12px",
                        background: colors.trackBg,
                        borderRadius: "999px",
                        overflow: "hidden"
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.min(100, item.count * 10)}%`,
                          height: "100%",
                          background: "#7c3aed",
                          borderRadius: "999px"
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={secondaryTextStyle}>
                {t("dashboard.empty.noStudioActivity", "No Studio activity yet.")}
              </p>
            )}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: "16px",
            marginTop: "16px"
          }}
        >
          <div style={cardStyle}>
            <h2 style={{ marginTop: 0, color: colors.text }}>
              {t("dashboard.charts.studioRunTrend", "Studio Run Trend")}
            </h2>
            {studioRunTrendData.length ? (
              <div>
                <svg width="100%" viewBox="0 0 420 220" style={{ display: "block" }}>
                  <line
                    x1="24"
                    y1="196"
                    x2="396"
                    y2="196"
                    stroke={colors.lineAxis}
                    strokeWidth="1"
                  />
                  <line
                    x1="24"
                    y1="24"
                    x2="24"
                    y2="196"
                    stroke={colors.lineAxis}
                    strokeWidth="1"
                  />
                  <path d={studioRunPath} fill="none" stroke="#8b5cf6" strokeWidth="3" />
                  {studioRunPoints.map((point, index) => (
                    <circle key={index} cx={point.x} cy={point.y} r="4" fill="#8b5cf6" />
                  ))}
                </svg>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "8px",
                    flexWrap: "wrap",
                    marginTop: "8px",
                    color: colors.textMuted,
                    fontSize: "12px"
                  }}
                >
                  {studioRunTrendData.map((item) => (
                    <span key={item.date}>
                      {item.date}: {item.total_runs}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <p style={secondaryTextStyle}>
                {t(
                  "dashboard.empty.noStudioRunHistory",
                  "No Studio run history yet. Use Studio tools to populate this chart."
                )}
              </p>
            )}
          </div>

          <div style={cardStyle}>
            <h2 style={{ marginTop: 0, color: colors.text }}>
              {t("dashboard.charts.studioUsageSummary", "Studio Usage Summary")}
            </h2>
            <div style={{ display: "grid", gap: "12px" }}>
              <div
                style={{
                  padding: "14px",
                  borderRadius: "14px",
                  background: colors.cardSubtle,
                  border: `1px solid ${colors.border}`
                }}
              >
                <div style={{ color: colors.textMuted, fontSize: "13px" }}>
                  {t("dashboard.summary.mostUsedTool", "Most Used Tool")}
                </div>
                <div
                  style={{
                    marginTop: "6px",
                    color: colors.text,
                    fontSize: "18px",
                    fontWeight: "700"
                  }}
                >
                  {studioUsageAnalytics.most_used_tool_label ||
                    formatToolName(studioUsageAnalytics.most_used_tool)}
                </div>
              </div>

              <div
                style={{
                  padding: "14px",
                  borderRadius: "14px",
                  background: colors.cardSubtle,
                  border: `1px solid ${colors.border}`
                }}
              >
                <div style={{ color: colors.textMuted, fontSize: "13px" }}>
                  {t("dashboard.summary.successfulRuns", "Successful Runs")}
                </div>
                <div
                  style={{
                    marginTop: "6px",
                    color: colors.text,
                    fontSize: "18px",
                    fontWeight: "700"
                  }}
                >
                  {studioUsageAnalytics.successful_runs || 0}
                </div>
              </div>

              <div
                style={{
                  padding: "14px",
                  borderRadius: "14px",
                  background: colors.cardSubtle,
                  border: `1px solid ${colors.border}`
                }}
              >
                <div style={{ color: colors.textMuted, fontSize: "13px" }}>
                  {t("dashboard.summary.failedRuns", "Failed Runs")}
                </div>
                <div
                  style={{
                    marginTop: "6px",
                    color: colors.text,
                    fontSize: "18px",
                    fontWeight: "700"
                  }}
                >
                  {studioUsageAnalytics.failed_runs || 0}
                </div>
              </div>

              <div
                style={{
                  padding: "14px",
                  borderRadius: "14px",
                  background: colors.cardSubtle,
                  border: `1px solid ${colors.border}`
                }}
              >
                <div style={{ color: colors.textMuted, fontSize: "13px" }}>
                  {t("dashboard.summary.averageGenerationTime", "Average Generation Time")}
                </div>
                <div
                  style={{
                    marginTop: "6px",
                    color: colors.text,
                    fontSize: "18px",
                    fontWeight: "700"
                  }}
                >
                  {studioUsageAnalytics.avg_generation_ms || 0}ms
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: "16px",
            marginTop: "16px"
          }}
        >
          {isPro ? (
            <div style={cardStyle}>
              <h2 style={{ marginTop: 0, color: colors.text }}>
                {t("dashboard.pro.dailyAverageScoreTrend", "Daily Average Score Trend")}
                {featureBadge(
                  t("dashboard.labels.proInsights", "PRO INSIGHTS"),
                  colors.greenText,
                  colors.greenBg
                )}
              </h2>
              {trendChartData.length ? (
                <div>
                  <svg width="100%" viewBox="0 0 420 220" style={{ display: "block" }}>
                    <line
                      x1="24"
                      y1="196"
                      x2="396"
                      y2="196"
                      stroke={colors.lineAxis}
                      strokeWidth="1"
                    />
                    <line
                      x1="24"
                      y1="24"
                      x2="24"
                      y2="196"
                      stroke={colors.lineAxis}
                      strokeWidth="1"
                    />
                    <path d={trendPath} fill="none" stroke="#3b82f6" strokeWidth="3" />
                    {trendPoints.map((point, index) => (
                      <circle key={index} cx={point.x} cy={point.y} r="4" fill="#3b82f6" />
                    ))}
                  </svg>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "8px",
                      flexWrap: "wrap",
                      marginTop: "8px",
                      color: colors.textMuted,
                      fontSize: "12px"
                    }}
                  >
                    {trendChartData.map((item) => (
                      <span key={item.day}>
                        {item.day}: {Number(item.avg_score).toFixed(1)}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <p style={secondaryTextStyle}>
                  {t(
                    "dashboard.empty.noTrendData",
                    "No trend data yet. Generate hooks over time to populate this chart."
                  )}
                </p>
              )}
            </div>
          ) : (
            lockedAnalyticsCard(
              t("dashboard.pro.dailyAverageScoreTrend", "Daily Average Score Trend"),
              t(
                "dashboard.pro.dailyAverageScoreTrendDescription",
                "Track score movement over time with richer historical insight and trend visibility."
              )
            )
          )}

          {isPro ? (
            <div style={cardStyle}>
              <h2 style={{ marginTop: 0, color: colors.text }}>
                {t("dashboard.pro.bestTopicsByAverageScore", "Best Topics by Average Score")}
                {featureBadge(t("dashboard.labels.pro", "PRO"))}
              </h2>
              {topicChartData.length ? (
                <div>
                  {topicChartData.map((item) => {
                    const width = Math.max(8, Math.min(100, item.average_score));
                    return (
                      <div key={item.topic} style={{ marginBottom: "14px" }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "10px",
                            marginBottom: "6px",
                            color: colors.textSoft,
                            flexWrap: "wrap"
                          }}
                        >
                          <span>{item.topic}</span>
                          <strong>{item.average_score.toFixed(1)}</strong>
                        </div>
                        <div
                          style={{
                            height: "12px",
                            background: colors.trackBg,
                            borderRadius: "999px",
                            overflow: "hidden"
                          }}
                        >
                          <div
                            style={{
                              width: `${width}%`,
                              height: "100%",
                              background: "#22c55e",
                              borderRadius: "999px"
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={secondaryTextStyle}>
                  {t("dashboard.empty.noTopicAnalytics", "No topic analytics yet.")}
                </p>
              )}
            </div>
          ) : (
            lockedAnalyticsCard(
              t("dashboard.pro.bestTopicsByAverageScore", "Best Topics by Average Score"),
              t(
                "dashboard.pro.bestTopicsByAverageScoreDescription",
                "Discover your strongest-performing topics with deeper topic-level scoring insight."
              )
            )
          )}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "14px",
            marginTop: "30px",
            marginBottom: "30px",
            ...cardStyle
          }}
        >
          <div>
            <label
              style={{
                fontWeight: "600",
                color: colors.textSoft,
                display: "block",
                marginBottom: "6px"
              }}
            >
              {t("dashboard.filters.platform", "Platform")}
            </label>
            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              style={inputStyle}
            >
              {platforms.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              style={{
                fontWeight: "600",
                color: colors.textSoft,
                display: "block",
                marginBottom: "6px"
              }}
            >
              {t("dashboard.filters.studioTool", "Studio Tool")}
            </label>
            <select
              value={toolFilter}
              onChange={(e) => setToolFilter(e.target.value)}
              style={inputStyle}
            >
              <option value={allToolsLabel}>
                {t("dashboard.filters.allStudioTools", "All Studio Tools")}
              </option>
              {availableStudioTools.map((tool) => (
                <option key={tool.toolType} value={tool.toolType}>
                  {tool.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              style={{
                fontWeight: "600",
                color: colors.textSoft,
                display: "block",
                marginBottom: "6px"
              }}
            >
              {t("dashboard.filters.sortBy", "Sort By")}
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={inputStyle}
            >
              <option value="score">{t("dashboard.filters.score", "Score")}</option>
              <option value="topic">{t("dashboard.filters.topic", "Topic")}</option>
            </select>
          </div>

          <div>
            <label
              style={{
                fontWeight: "600",
                color: colors.textSoft,
                display: "block",
                marginBottom: "6px"
              }}
            >
              {t("dashboard.filters.searchTopic", "Search Topic")}
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("dashboard.filters.searchPlaceholder", "Type to search...")}
              style={inputStyle}
            />
          </div>
        </div>

        {!hasAnyContent ? (
          <div
            style={{
              ...cardStyle,
              marginTop: "24px",
              background: colors.emptyStateBg
            }}
          >
            <h2 style={{ marginTop: 0, color: colors.text }}>
              {t("dashboard.empty.noContentTitle", "No content generated yet")}
            </h2>
            <p style={{ color: colors.textMuted, lineHeight: "1.7" }}>
              {t(
                "dashboard.empty.noContentDescription",
                "Start using Studio tools to generate hooks, titles, captions, and scripts. Your personal analytics will appear here as you create and save content."
              )}
            </p>
            <div style={{ marginTop: "14px" }}>
              <Link
                to="/studio"
                style={{
                  display: "inline-block",
                  textDecoration: "none",
                  background: colors.buttonDarkBg,
                  color: colors.buttonDarkText,
                  padding: "10px 14px",
                  borderRadius: "10px",
                  fontWeight: "700"
                }}
              >
                {t("dashboard.actions.goToStudio", "Go to Studio")}
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div style={{ marginTop: "40px" }}>
              <h2 style={sectionTitleStyle}>
                {t("dashboard.sections.favoriteHooks", "Favorite Hooks")}
              </h2>
              {filteredFavoriteHooks.length ? (
                <ul style={{ paddingLeft: 0 }}>
                  {filteredFavoriteHooks.map((hook) => renderHookItem(hook))}
                </ul>
              ) : (
                <p style={secondaryTextStyle}>
                  {t("dashboard.empty.noFavoriteHooks", "No favorite hooks yet.")}
                </p>
              )}
            </div>

            <div style={{ marginTop: "40px" }}>
              <h2 style={sectionTitleStyle}>
                {t("dashboard.sections.strongestHookPerTopic", "Strongest Hook per Topic")}
              </h2>
              <ul style={{ paddingLeft: 0 }}>
                {Object.values(strongestPerTopic).map((hook) =>
                  renderHookItem(hook, true)
                )}
              </ul>
            </div>

            <div style={{ marginTop: "40px" }}>
              <h2 style={sectionTitleStyle}>{t("dashboard.sections.topHooks", "Top Hooks")}</h2>
              {filteredTopHooks.length ? (
                <ul style={{ paddingLeft: 0 }}>{filteredTopHooks.map(renderHookItem)}</ul>
              ) : (
                <p style={secondaryTextStyle}>
                  {t("dashboard.empty.noRecentActivity", "No recent activity yet.")}
                </p>
              )}
            </div>

            <div style={{ marginTop: "40px" }}>
              <h2 style={sectionTitleStyle}>
                {t("dashboard.sections.recentHooks", "Recent Hooks")}
              </h2>
              {filteredRecentHooks.length ? (
                <ul style={{ paddingLeft: 0 }}>
                  {filteredRecentHooks.map(renderHookItem)}
                </ul>
              ) : (
                <p style={secondaryTextStyle}>
                  {t("dashboard.empty.noRecentActivity", "No recent activity yet.")}
                </p>
              )}
            </div>

            <div style={{ marginTop: "40px" }}>
              <h2 style={sectionTitleStyle}>
                {t("dashboard.sections.recentStudioAssets", "Recent Studio Assets")}
              </h2>
              {filteredRecentStudioAssets.length ? (
                <ul style={{ paddingLeft: 0 }}>
                  {filteredRecentStudioAssets.map((asset) => renderStudioAssetItem(asset))}
                </ul>
              ) : (
                <p style={secondaryTextStyle}>
                  {t("dashboard.empty.noStudioAssetsSaved", "No Studio assets saved yet.")}
                </p>
              )}
            </div>

            <div style={{ marginTop: "40px" }}>
              <h2 style={sectionTitleStyle}>
                {t("dashboard.sections.combinedRecentActivity", "Combined Recent Activity")}
              </h2>
              {filteredCombinedActivity.length ? (
                <ul style={{ paddingLeft: 0 }}>
                  {filteredCombinedActivity.map((item) =>
                    item.type === "hook"
                      ? renderHookItem(item)
                      : renderStudioAssetItem(item)
                  )}
                </ul>
              ) : (
                <p style={secondaryTextStyle}>
                  {t("dashboard.empty.noRecentActivity", "No recent activity yet.")}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

function MiniFounderCard({ label, value, colors }) {
  return (
    <div
      style={{
        background: colors.founderPanelBg,
        border: `1px solid ${colors.border}`,
        borderRadius: "14px",
        padding: "14px",
        boxShadow: colors.shadowSoft
      }}
    >
      <div style={{ color: colors.textMuted, fontSize: "13px" }}>{label}</div>
      <div
        style={{
          marginTop: "6px",
          color: colors.text,
          fontSize: "24px",
          fontWeight: "800"
        }}
      >
        {value}
      </div>
    </div>
  );
}

export default Dashboard;